import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "../../../utils/toast";

import {
  createCheckoutSession,
  downloadDealOverviewPdf,
  downloadDealPaymentReceiptPdf,
  getDealAgreement,
  getDealById,
  sendMessage,
  signDealAgreement,
  updateDealStatus,
} from "../../../services/api";
import { ChatRequestDto, DealAgreementResponseDto, DealResponseDto } from "../../../types/types";
import type { DealFormData } from "../createDeal/dealFormTypes";
import { asRecord } from "../../../utils/typeGuards";
import {
  getOrganizerApprovalPendingMessage,
  hasRequiredDocumentsWaitingForUpload,
} from "../../../utils/deals/dealHelpers";
import { downloadBlob, uploadSignature } from "../../../utils/agreementHelpers";
import { getErrorMessage } from "../../../utils/utilMethods";
import {
  cleanDealId,
  encodeDealStatusMessage,
} from "../../../utils/chat/dealStatusMessage";
import DealViewContent from "./dealViewContent";
import { useDealFieldRenderers } from "./useDealFieldRenderers";

const ViewDealPage: React.FC = () => {
  const { t, i18n } = useTranslation("viewDeal");
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [deal, setDeal] = useState<DealResponseDto | null>(null);
  const [loading, setLoading] = useState(true); 
  const [activeTab, setActiveTab] = useState("general");
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const [downloadingReceipt, setDownloadingReceipt] = useState(false);
  const [downloadingAgreement, setDownloadingAgreement] = useState(false);
  const [dealActionProcessing, setDealActionProcessing] = useState(false);
  const [agreement, setAgreement] = useState<DealAgreementResponseDto | null>(null);

  const paymentPollRef = useRef<number | null>(null);
  const paymentRefreshGuardRef = useRef(false);
  const paymentLocksRef = useRef<Set<number>>(new Set());

  const rawLoginResponse = localStorage.getItem("loginResponse") || "{}";
  const loginResponse = JSON.parse(rawLoginResponse) as Record<string, unknown>;
  const currentUserId = loginResponse.id != null ? String(loginResponse.id) : "";
  const loggedInUserName = String(loginResponse.username || "");
  const userProfileType = String(loginResponse.profileType || "");

  const isFighter = userProfileType === "FIGHTER";

  const isManager = deal && currentUserId
    ? (asRecord(deal.acceptedBy).id?.toString() || "") !== currentUserId
    : false;

  const hasUserRated = deal?.ratingResponse && currentUserId
    ? deal.ratingResponse.some(
        (rating) => asRecord(rating.ratedBy).id?.toString() === currentUserId,
      )
    : false;

  const canPayComponents = [
    "ACCEPTED",
    "CONFIRMED",
    "WAITING_FOR_RESULT",
    "RESULT_UPDATED",
    "RESULT_CONFIRMED",
    "FIGHTER_RATED",
    "MANAGER_RATED",
  ].includes(deal?.status || "");

  const refreshDeal = useCallback(async (): Promise<DealResponseDto | null> => {
    const dealId = cleanDealId(id);
    if (!dealId) {
      return null;
    }

    try {
      const updatedDeal = await getDealById(dealId);
      setDeal(updatedDeal);
      return updatedDeal;
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, t("failedToRefreshDeal")));
      return null;
    }
  }, [id, t]);

  const sendDealActionMessage = async (
    updatedDeal: DealResponseDto,
    action: "ACCEPTED" | "REJECTED",
  ) => {
    try {
      const managerProfile =
        updatedDeal.initiatedBy || asRecord(updatedDeal).createdBy;
      const fighterProfile = updatedDeal.acceptedBy;
      const managerId = managerProfile?.id?.toString();
      const fighterId = fighterProfile?.id?.toString();
      const receiverId = currentUserId === fighterId ? managerId : fighterId;

      if (!receiverId) return;

      // NOTE: always posts to the INDIVIDUAL chat, even for deals that belong to a group chat.
      const payload: ChatRequestDto = {
        message: encodeDealStatusMessage(updatedDeal, action),
        fileMetadataList: [],
        type: "CONTENT",
        chatType: "INDIVIDUAL",
        receiverId,
      };

      await sendMessage(payload);
    } catch (error) {
      console.error("Error sending deal action message:", error);
    }
  };

  const handleAcceptDeal = async () => {
    if (!deal?.id || dealActionProcessing) return;

    if (hasRequiredDocumentsWaitingForUpload(deal)) {
      toast.error(
        t("uploadAllRequiredDocumentsBeforeAcceptingDeal"),
      );
      return;
    }

    const organizerApprovalPendingMessage =
      getOrganizerApprovalPendingMessage(deal);
    if (organizerApprovalPendingMessage) {
      toast.error(organizerApprovalPendingMessage);
      return;
    }

    if (agreement?.status !== "FULLY_SIGNED") {
      toast.error(
        t("signAgreementBeforeAccepting"),
      );
      return;
    }

    setDealActionProcessing(true);
    try {
      const updatedDeal = await updateDealStatus(deal.id, "ACCEPTED");
      setDeal(updatedDeal);
      await sendDealActionMessage(updatedDeal, "ACCEPTED");
      toast.success(
        t("dealAcceptedSuccessfully"),
      );
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, t("failedToUpdateDealStatus")));
    } finally {
      setDealActionProcessing(false);
    }
  };

  const openProgressDealResponse = () => {
    setActiveTab("progress");

    window.setTimeout(() => {
      document
        .getElementById("deal-progress-accepted-action")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
  };

  const handleDeclineDeal = async () => {
    if (!deal?.id || dealActionProcessing) return;

    setDealActionProcessing(true);
    try {
      const updatedDeal = await updateDealStatus(deal.id, "REJECTED");
      setDeal(updatedDeal);
      await sendDealActionMessage(updatedDeal, "REJECTED");
      toast.success(
        t("dealDeclinedSuccessfully"),
      );
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, t("failedToUpdateDealStatus")));
    } finally {
      setDealActionProcessing(false);
    }
  };

  const formData = (deal?.formData ?? {}) as DealFormData;
  const {
    renderDynamicObject,
    renderSummary,
    tr,
    uploadRequiredDocument,
    reviewRequiredDocument,
  } = useDealFieldRenderers({
    deal,
    formData,
    t,
    refreshDeal,
    onOpenDealResponseActions: openProgressDealResponse,
  });

  const refreshDealAfterPayment = useCallback(async () => {
    if (paymentRefreshGuardRef.current) return;
    paymentRefreshGuardRef.current = true;
    try {
      await refreshDeal();
    } finally {
      window.setTimeout(() => {
        paymentRefreshGuardRef.current = false;
      }, 1200);
    }
  }, [refreshDeal]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isPaymentSuccessUrl =
      window.location.pathname.includes("/payment/success");
    const hasPaidQueryParam = !!urlParams.get("paid");

    if (isPaymentSuccessUrl || hasPaidQueryParam) {
      void refreshDealAfterPayment();
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, "", cleanUrl);
    }
  }, [refreshDealAfterPayment]);

  useEffect(() => {
    const handlePostMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      const messageType = event.data?.type ?? event.data?.status;
      if (messageType === "PAYMENT_SUCCESS") {
        void refreshDealAfterPayment();
        toast.success(t("paymentSuccessful"));
      }
    };

    window.addEventListener("message", handlePostMessage);
    return () => window.removeEventListener("message", handlePostMessage);
  }, [refreshDealAfterPayment, t]);

  useEffect(() => {
    return () => {
      if (paymentPollRef.current) {
        window.clearInterval(paymentPollRef.current);
        paymentPollRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const dealId = cleanDealId(id);
    if (!dealId) {
      setLoading(false);
      setDeal(null);
      return;
    }

    const fetchDeal = async () => {
      try {
        setLoading(true);
        const dealResponse = await getDealById(dealId);
        setDeal(dealResponse);
      } catch (error: unknown) {
        toast.error(getErrorMessage(error, t("failedToLoadDeal")));
        navigate("/deals");
      } finally {
        setLoading(false);
      }
    };

    fetchDeal();
  }, [id, navigate, t]);

useEffect(() => {
  const dealId = cleanDealId(id);
  if (!dealId) return;

  setAgreement(null); 

  const loadAgreement = async () => {
    try {
      setAgreement(await getDealAgreement(dealId));
    } catch (error) {
      
    }
  };
  loadAgreement();
}, [id]);

  useEffect(() => {
    const storedLanguage = localStorage.getItem("i18nextLng") || "en";
    i18n.changeLanguage(storedLanguage);
  }, [i18n]);

  useEffect(() => {
    if (loading || !deal) return;

    const urlParams = new URLSearchParams(window.location.search);
    const highlightSection = urlParams.get("highlight");
    if (!highlightSection) return;

    const highlightMap: Record<string, { tab: string; targetId: string }> = {
      documentRequirement: {
        tab: "general",
        targetId: "document-requirement-section",
      },
      requiredDocuments: {
        tab: "progress",
        targetId: "deal-required-documents-section",
      },
    };

    const highlight = highlightMap[highlightSection] || {
      tab: "general",
      targetId: highlightSection,
    };

    setActiveTab(highlight.tab);

    window.setTimeout(() => {
      const element = document.getElementById(highlight.targetId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 300);

    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, "", cleanUrl);
  }, [loading, deal]);

  const handleSignAgreement = async (signatureDataUrl: string) => {
    if (!deal?.id || dealActionProcessing) return;
    setDealActionProcessing(true);
    try {
      const signatureUrl = await uploadSignature(signatureDataUrl);
      await signDealAgreement(deal.id, signatureUrl);
      // Refresh agreement and deal together so the UI updates right after signing
      const [updatedAgreement] = await Promise.all([
        getDealAgreement(deal.id),
        refreshDeal(),
      ]);
      setAgreement(updatedAgreement);
      toast.success(
        t("agreementSignedSuccessfully"),
      );
      // Take the user back to the Progress tab so they know what to do next
      openProgressDealResponse();
    } catch (error: unknown) {
      toast.error(
        getErrorMessage(
          error,
          t("failedToSignAgreement"),
        ),
      );
    } finally {
      setDealActionProcessing(false);
    }
  };

  const handleDownloadSummaryPdf = async () => {
    if (!deal?.id) return;
    setDownloadingInvoice(true);
    try {
      const pdfBlob = await downloadDealOverviewPdf(deal.id);
      downloadBlob(pdfBlob, `DEAL_SUMMARY_${deal.id}.pdf`);
    } catch {
      toast.error(t("failedToGenerateInvoice"));
    } finally {
      setDownloadingInvoice(false);
    }
  };

  const handleDownloadReceipt = async () => {
    if (!deal?.id) return;
    setDownloadingReceipt(true);
    try {
      const receiptBlob = await downloadDealPaymentReceiptPdf(deal.id);
      downloadBlob(receiptBlob, `RECEIPT_${deal.id}.pdf`);
    } catch {
      toast.error(t("noPaymentReceiptAvailable"));
    } finally {
      setDownloadingReceipt(false);
    }
  };

  const handleDownloadAgreement = async () => {
    if (!deal?.agreementFile?.fileUrl) return;
    setDownloadingAgreement(true);
    try {
      const agreementResponse = await fetch(deal.agreementFile.fileUrl);
      if (!agreementResponse.ok) throw new Error(t("failedToDownloadAgreement"));
      const agreementBlob = await agreementResponse.blob();
      downloadBlob(
        agreementBlob,
        deal.agreementFile.fileName || "agreement.pdf",
      );
    } catch (error: unknown) {
      toast.error(
        getErrorMessage(error, t("failedToDownloadAgreement")),
      );
    } finally {
      setDownloadingAgreement(false);
    }
  };

  const handlePayComponent = async (componentPaymentId: number) => {
    if (!deal?.id || !loggedInUserName) return;
    if (paymentLocksRef.current.has(componentPaymentId)) return;

    const componentPayment = deal.componentPayments?.find(
      (payment) => Number(payment.id) === Number(componentPaymentId),
    );

    if (!componentPayment || componentPayment.status !== "PENDING") {
      toast.error(t("paymentNotAvailableForCheckout"));
      return;
    }

    paymentLocksRef.current.add(componentPaymentId);

    try {
      const checkoutResponse = await createCheckoutSession(
        loggedInUserName,
        deal.id,
        componentPaymentId,
      );

      const checkoutResponseRecord = asRecord(checkoutResponse);
      const checkoutUrl = String(
        checkoutResponseRecord.checkoutUrl ?? checkoutResponseRecord.url ?? "",
      );

      if (!checkoutUrl) {
        throw new Error(t("checkoutUrlNotAvailable"));
      }

      if (paymentPollRef.current) {
        window.clearInterval(paymentPollRef.current);
        paymentPollRef.current = null;
      }

      const paymentWindow = window.open(checkoutUrl, "_blank");

      if (!paymentWindow) {
        window.location.assign(checkoutUrl);
        return;
      }

      paymentPollRef.current = window.setInterval(() => {
        if (paymentWindow.closed) {
          if (paymentPollRef.current) {
            window.clearInterval(paymentPollRef.current);
            paymentPollRef.current = null;
          }
          void refreshDealAfterPayment();
        }
      }, 1000); 
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, t("componentPaymentFailed")));
    } finally {
      paymentLocksRef.current.delete(componentPaymentId);
    }
  };

  if (loading)
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );

  if (!deal)
    return (
      <div className="text-center p-10">
        <p>{t("dealNotFound")}</p>
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 px-3 py-2 bg-gray-100 rounded"
        >
          {t("back")}
        </button>
      </div>
    );

return (
    <DealViewContent
      deal={deal}
      formData={formData}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      t={t}
      isFighter={isFighter}
      canPayComponents={canPayComponents}
      handlePayComponent={handlePayComponent}
      currentUserId={currentUserId}
      isManager={isManager}
      hasUserRated={hasUserRated}
      refreshDeal={refreshDeal}
      downloadingInvoice={downloadingInvoice}
      downloadingReceipt={downloadingReceipt}
      downloadingAgreement={downloadingAgreement}
      handleDownloadSummaryPdf={handleDownloadSummaryPdf}
      handleDownloadReceipt={handleDownloadReceipt}
      handleDownloadAgreement={handleDownloadAgreement}
      agreement={agreement}
      onSignAgreement={handleSignAgreement}
      onAcceptDeal={handleAcceptDeal}
      onDeclineDeal={handleDeclineDeal}
      isDealActionProcessing={dealActionProcessing}
      renderDynamicObject={renderDynamicObject}
      renderSummary={renderSummary}
      uploadRequiredDocument={uploadRequiredDocument}
      reviewRequiredDocument={reviewRequiredDocument}
      tr={tr}
    />
  );
};

export default ViewDealPage;
